// Throttle function: ensures `func` runs at most once every `limit` ms
export const throttle = (func: (...args: any[]) => void, limit: number) => {
    let lastFunc: ReturnType<typeof setTimeout> | null = null;
    let lastRan: number;

    return function (this: any, ...args: any[]) {
        if (!lastRan) {
            func.apply(this, args);
            lastRan = Date.now();
        } else {
            if (lastFunc) clearTimeout(lastFunc);
            lastFunc = setTimeout(() => {
                if (Date.now() - lastRan >= limit) {
                    func.apply(this, args);
                    lastRan = Date.now();
                }
            }, limit - (Date.now() - lastRan));
        }
    };
};

// Debounce function: delays calling `func` until `wait` ms have passed without a new call
export function debounce<T extends (...args: any[]) => void>(
    func: T,
    wait: number
): (...args: Parameters<T>) => void {
    let timeout: ReturnType<typeof setTimeout> | null = null;

    return function (...args: Parameters<T>) {
        if (timeout) {
            clearTimeout(timeout);
        }

        timeout = setTimeout(() => {
            func(...args);
        }, wait);
    };
}

// Deep merge two objects recursively
export function deepMerge(obj1: any, obj2: any): any {
    const output = { ...obj1 };

    for (const key in obj2) {
        if (Object.prototype.hasOwnProperty.call(obj2, key)) {
            if (
                typeof obj2[key] === 'object' &&
                obj2[key] !== null &&
                obj1[key] &&
                typeof obj1[key] === 'object'
            ) {
                output[key] = deepMerge(obj1[key], obj2[key]);
            } else {
                output[key] = obj2[key];
            }
        }
    }

    return output;
}

// Generate a unique ID based on timestamp and random number
export function uniqueID(): string {
    return (Date.now() + Math.floor(Math.random() * 1000)).toString();
}
